import {useEffect, useState} from 'react'
import {createPortal} from 'react-dom'
import {Package} from 'lucide-react'

interface ProductThumbnailProps {
  src?: string
  alt: string
  ean?: string
  size?: 'sm' | 'md'
}

const sizeClasses = {
  sm: 'h-10 w-10',
  md: 'h-12 w-12',
}

export function ProductThumbnail({
  src,
  alt,
  ean,
  size = 'md',
}: ProductThumbnailProps) {
  const [failed, setFailed] = useState(false)
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    setFailed(false)
    setExpanded(false)
  }, [src])

  const hasImage = Boolean(src && !failed)

  return (
    <>
      <div
        className={`${sizeClasses[size]} relative flex flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm`}
        onMouseEnter={() => hasImage && setExpanded(true)}
        onMouseLeave={() => setExpanded(false)}
        onFocus={() => hasImage && setExpanded(true)}
        onBlur={() => setExpanded(false)}
        tabIndex={hasImage ? 0 : -1}
        aria-label={hasImage ? `Ampliar imagem de ${alt}` : `Imagem indisponível para ${alt}`}
      >
        {hasImage ? (
          <img
            src={src}
            alt={alt}
            loading='lazy'
            referrerPolicy='no-referrer'
            className='h-full w-full object-contain p-1'
            onError={() => {
              setFailed(true)
              setExpanded(false)
            }}
          />
        ) : (
          <Package className='h-5 w-5 text-gray-300' aria-hidden='true' />
        )}
      </div>

      {hasImage &&
        expanded &&
        typeof document !== 'undefined' &&
        createPortal(
          <div className='pointer-events-none fixed inset-0 z-[10000] flex items-center justify-center bg-slate-950/45 p-8'>
            <div className='max-w-[min(520px,85vw)] rounded-2xl border border-white/60 bg-white p-4 shadow-2xl'>
              <div className='flex min-h-72 min-w-72 items-center justify-center rounded-xl bg-white'>
                <img
                  src={src}
                  alt={alt}
                  referrerPolicy='no-referrer'
                  className='max-h-[70vh] max-w-[70vw] object-contain'
                  onError={() => {
                    setFailed(true)
                    setExpanded(false)
                  }}
                />
              </div>
              <div className='mt-3 border-t border-gray-100 pt-3 text-center'>
                <p className='max-w-md truncate text-sm font-semibold text-gray-900'>{alt}</p>
                {ean && <p className='mt-0.5 text-xs text-gray-500'>EAN: {ean}</p>}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  )
}
